/* eslint-disable @typescript-eslint/consistent-type-definitions */
import { InjectionToken, Signal, computed, inject } from '@angular/core';
import {
	EmptyFeatureResult,
	SignalStoreFeature,
	WritableStateSource,
	patchState,
	signalStoreFeature,
	withComputed,
	withMethods,
	withState,
} from '@ngrx/signals';
import { EMPTY, Observable, Subject, auditTime, catchError, combineLatest, distinctUntilChanged, filter, firstValueFrom, from, map, of, skip, switchMap, take, tap, throwError, withLatestFrom } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

import { createDefaultListStateAnySort, emptyList } from '../../functions/utils.list.function';
import { SortedFilteredList } from '../../classes/sorted-filtered-list.class';
import { makeSortedList } from '../../functions/utils.sorted-list.function';
import { IPreferedPageSizes } from '../../models/prefered-page-sizes.model';
import { encodeFiltersQuery } from '../../functions/utils.filters.function';
import { ListActionHandlerBase } from '../../classes/list-handler.class';
import { SignalLoader } from '../../classes/signal-loader.class';
import { IUserFilters } from '../../models/user-filters.model';
import { IList, IListState } from '../../models/list.model';
import { IUserPrefs } from '../../models/user-prefs.model';
import { uuid } from '../../functions/utils.function';
import { Loader } from '../../classes/loader.class';
import { IFilter } from '../../models/filter.model';
import { AnySort } from '../../models/sort.model';
import { AppConstants } from '../../config';

export interface UserPrefsConnector {
	currentPreferences: Observable<IUserPrefs>;
	updatePreferences: (prefs : IUserPrefs) => Observable<unknown>;
	updatePageSizes: (prefs : IPreferedPageSizes) => Observable<unknown>;
	updateFilters: (filters: IUserFilters) => Observable<unknown>;
}

type CallState = 'init' | 'loading' | 'loaded' | { error: string };
export type ListPaging<S> = IListState<S>;

export type NamedList<Item, Collection extends string> = {
	[K in Collection as `${K}List`]: IList<Item>;
};

export type NamedListStateSlice<Collection extends string> = {
	[K in Collection as `${K}CallState`]: CallState;
};

export type NamedListPagingSlice<S, Collection extends string> = {
	[K in Collection as `${K}Paging`]: ListPaging<S>;
};

export type NamedListFiltersSlice<Collection extends string> = {
	[K in Collection as `${K}Filters`]: IFilter[];
};

export type NamedListCompleteSlice<Item, Sort, Collection extends string> =
	& NamedList<Item, Collection>
	& NamedListPagingSlice<Sort, Collection>
	& NamedListFiltersSlice<Collection>
	& NamedListStateSlice<Collection>;

export interface ListStateSlice<Item, Sort> {
	list: IList<Item>;
	callState: CallState;
	paging: ListPaging<Sort>;
	filters: IFilter[];
}

export type NamedListStateMethods<Sort, Prop extends string> = {
	[K in Prop as `${K}Reset`]: () => Promise<void>;
} & {
	[K in Prop as `${K}SetPaging`]: (paging: ListPaging<Sort>) => Promise<void>;
} & {
	[K in Prop as `${K}FiltersChanged`]: () => Observable<void>;
} & {
	[K in Prop as `${K}AddFilters`]: (filters: IFilter[]) => Promise<void>;
} & {
	[K in Prop as `${K}RemoveFilters`]: (filters: IFilter[]) => Promise<void>;
} & {
	[K in Prop as `${K}ResetFilters`]: () => Promise<void>;
} & {
	[K in Prop as `${K}Loader`]: () => Loader;
} & {
	[K in Prop as `${K}Handler`]: (filtersBuilderComponent?: any) => ListActionHandlerBase;
};

export type NamedListStateSignals<Item, Sort, Prop extends string> = {
	[K in Prop as `${K}Loading`]: Signal<boolean>;
} & {
	[K in Prop as `${K}Loaded`]: Signal<boolean>;
} & {
	[K in Prop as `${K}Error`]: Signal<string | null>;
} & {
	[K in Prop as `${K}SortedList`]: Signal<SortedFilteredList<Item, Sort>>;
} & {
	[K in Prop as `${K}Domain`]: Signal<string>;
} & {
	[K in Prop as `${K}ListState`]: Signal<IListState<Sort>>;
} & {
	[K in Prop as `${K}IsInBulkLimit`]: Signal<boolean>;
};

export type ListStateMethods<Sort> = {
	reset: () => Promise<void>;
	loader: () => Loader;
	handler: (filtersBuilderComponent?: any) => ListActionHandlerBase;
	setPaging: (paging: ListPaging<Sort>) => void;
	filtersChanged: () => Observable<void>;
	addFilters: (filters: IFilter[]) => void;
	removeFilters: (filters: IFilter[]) => void;
	resetFilters: () => void;
}

export type ListStateSignals<Item, Sort> = {
	loading: Signal<boolean>;
	loaded: Signal<boolean>;
	error: Signal<string | null>;
	sortedList: Signal<SortedFilteredList<Item, Sort>>;
	domain: Signal<string>;
	listState: Signal<IListState<Sort>>;
	isInBulkLimit: Signal<boolean>;
}

export function getListStateKeys(config: { collection?: string }) {
	const c = config?.collection;
	return {
		resetKey: c ? `${c}Reset`: 'reset',
		listKey: c ? `${c}List`: 'list',
		listDomainKey: c ? `${c}Domain`: 'domain',
		callStateKey: c ? `${c}CallState`: 'callState',
		listStateKey: c ? `${c}ListState`: 'listState',
		loaderKey: c ? `${c}Loader`: 'loader',
		loadingKey: c ? `${c}Loading`: 'loading',
		loadedKey: c ? `${c}Loaded`: 'loaded',
		errorKey: c ? `${c}Error`: 'error',
		pagingKey: c ? `${c}Paging`: 'paging',
		filtersKey: c ? `${c}Filters`: 'filters',
		sortedListKey: c ? `${c}SortedList`: 'sortedList',
		setPagingKey: c ? `${c}SetPaging`: 'setPaging',
		filtersChangedKey: c ? `${c}FiltersChanged`: 'filtersChanged',
		addFiltersKey: c ? `${c}AddFilters`: 'addFilters',
		removeFiltersKey: c ? `${c}RemoveFilters`: 'removeFilters',
		resetFiltersKey: c ? `${c}ResetFilters`: 'resetFilters',
		listHandlerKey: c ? `${c}Handler`: 'handler',
		isInBulkLimit: c ? `${c}IsInBulkLimit`: 'isInBulkLimit',
	};
}

export function withListState<Item, Sort, Collection extends string>(config: {
	collection: Collection;
	defaultSort?: AnySort<Sort>;
	defaultPageSize?: number;
	domain?: string;
	prefsManagerToken?: InjectionToken<UserPrefsConnector>;
}): SignalStoreFeature<
	EmptyFeatureResult,
	{
		state: NamedListCompleteSlice<Item, Sort, Collection>;
		props: NamedListStateSignals<Item, Sort, Collection>;
		methods: NamedListStateMethods<Sort, Collection>;
	}
>;
export function withListState<Item, Sort>(config: {
	defaultSort?: AnySort<Sort>;
	defaultPageSize?: number;
	domain?: string;
	prefsManagerToken?: InjectionToken<UserPrefsConnector>;
}): SignalStoreFeature<
	EmptyFeatureResult,
	{
		state: ListStateSlice<Item, Sort>;
		props: ListStateSignals<Item, Sort>;
		methods: ListStateMethods<Sort>;
	}
>;
export function withListState<Item, Sort, Collection extends string>(config: {
	collection: Collection;
	defaultSort: AnySort<Sort>;
	defaultPageSize?: number;
	bulkLimit?: number;
	domain?: string;
	prefsManagerToken?: InjectionToken<UserPrefsConnector>;
}): SignalStoreFeature {
	const k = getListStateKeys(config)
	const defaultPageSize = config?.defaultPageSize ?? AppConstants.LIST_PAGE_SIZE_DEFAULT
	const bulkLimit: number = config?.bulkLimit ?? AppConstants.BULK_EDIT_MAX_SIZE
	const listDomain: string = config?.domain || uuid()

	return signalStoreFeature(

		withState({
			[k.callStateKey]: 'init',
			[k.listKey]: emptyList(),
			[k.pagingKey]: createDefaultListStateAnySort(config.defaultSort ?? [], defaultPageSize),
			[k.filtersKey]: [],
		}),

		withComputed((state: Record<string, Signal<unknown>>) => {
			const list = state[k.listKey] as Signal<IList<Item>>;
			const callState = state[k.callStateKey] as Signal<CallState>;
			const paging = state[k.pagingKey] as Signal<ListPaging<Sort>>;
			const filters = state[k.filtersKey] as Signal<IFilter[]>;

			return {
				[k.loadingKey]: computed(() => callState() === 'loading'),
				[k.loadedKey]: computed(() => callState() === 'loaded'),
				[k.listDomainKey]: computed(() => listDomain),
				[k.errorKey]: computed(() => {
					const v = callState();
					return typeof v === 'object' ? v.error : null;
				}),
				[k.listStateKey]: computed(() => ({ ...paging(), filters: filters() })),
				[k.sortedListKey]: computed(() => makeSortedList<IList<Item>, Item, Sort>(list(), paging())),
				[k.isInBulkLimit]: computed(() => list().paging?.totalItemCount <= bulkLimit),
			};
		}),

		withMethods(state => {
			const prefsManager = (config?.domain && config.prefsManagerToken)
				? connectUserPreferences(state, config.collection, listDomain, config.prefsManagerToken)
				: null;
			const loader = new SignalLoader(
				state[k.loadingKey] as Signal<boolean>,
				state[k.errorKey] as Signal<string>);

			return {
				[k.resetKey]: async () => patchState(state, {
					[k.callStateKey]: 'init',
					[k.listKey]: emptyList(),
					[k.pagingKey]: createDefaultListStateAnySort(config.defaultSort ?? [], defaultPageSize),
					[k.filtersKey]: [],
				}),
				[k.loaderKey]: () => loader,
				[k.setPagingKey]: (paging: ListPaging<Sort>) => {
					patchState(state, setListPaging(paging, config.collection))
					if (prefsManager) prefsManager.updatePageSizes({ [listDomain]: +state[k.pagingKey]() || AppConstants.LIST_PAGE_SIZE_DEFAULT })
					if ((state as any).usageEvent) (state.usageEvent as (name: string) => void)('setPaging')
					return Promise.resolve()
				},
				[k.addFiltersKey]: filters => {
					patchState(state, addListFilters(filters, config.collection))
					// if (prefsManager) prefsManager.updateFilters({ [listDomain]: state[k.filtersKey]() as any[] || [] })
					if ((state as any).usageEvent) (state.usageEvent as (name: string) => void)('addFilters')
					return Promise.resolve()
				},
				[k.removeFiltersKey]: filters => {
					patchState(state, removeListFilters(filters, config.collection) as any) // FIXME: any
					// if (prefsManager) prefsManager.updateFilters({ [listDomain]: state[k.filtersKey]() as any[] || [] })
					if ((state as any).usageEvent) (state.usageEvent as (name: string) => void)('removeFilters')
					return Promise.resolve()
				},
				[k.resetFiltersKey]: _ => {
					patchState(state, resetListFilters(config.collection))
					// if (prefsManager) prefsManager.updateFilters({ [listDomain]: [] })
					if ((state as any).usageEvent) (state.usageEvent as (name: string) => void)('resetFilters')
					return Promise.resolve()
				}
			}
		}),

		withMethods(({ ...state }) => {
			const list = state[k.sortedListKey] as unknown as Signal<IList<Item>>;
			const filters = state[k.filtersKey] as unknown as Signal<IFilter[]>;
			const loading = state[k.loadingKey] as unknown as Signal<boolean>;

			// Prepare a stream of observable filters changes
			// Bug 36467: La sélection des clients n'est pas actualisée si on modifie les filtres
			const filtersChanged$ = combineLatest([
				toObservable(filters) as Observable<IFilter[]>,
				toObservable(loading),
				toObservable(list),
			]).pipe(
				auditTime(10),
				filter(([_, loading]) => !loading),
				map(([f]) => encodeFiltersQuery(f)),
				distinctUntilChanged(),
				skip(1),
				tap(l => {
					console.debug(`[list-store] filters changed: ${l}`)
				})
			);

			return {
				[k.filtersChangedKey]: () => filtersChanged$,

				[k.listHandlerKey]: (filtersBuilderComponent?) => {
					const handler = new ListActionHandlerBase().withPagingDispatcher(props => state[k.setPagingKey](props))
					return !filtersBuilderComponent
						? handler
						: handler.withFilterBuilder(filtersBuilderComponent,
							state[k.addFiltersKey] as (f: IFilter[]) => void,
							state[k.removeFiltersKey] as (f: IFilter[]) => void)
				}
			}
		})
	);
}

function connectUserPreferences(state, collection, domain, prefsManagerToken) {
	const app: UserPrefsConnector = inject(prefsManagerToken);
	const preferences$ = new Subject<IUserPrefs>();
	const filters$ = new Subject<IUserFilters>();

	preferences$.pipe(auditTime(500), withLatestFrom(app.currentPreferences),
		switchMap(([arg, current]) => app.updatePreferences({ ...current, pageSizes: {
			...current.pageSizes||{}, ...arg.pageSizes
		}})),
		catchError(e => {
			console.debug(`[user-prefs] cannot update pages sizes: ${e.toString()}`)
			return EMPTY
		}))
		.subscribe()

	filters$.pipe(auditTime(500), withLatestFrom(app.currentPreferences),
		switchMap(([arg, _current]) => app.updateFilters(arg)),
		catchError(e => {
			console.debug(`[user-prefs] cannot update filters: ${e.toString()}`)
			return EMPTY
		}))
		.subscribe()

	const prefsManager: UserPrefsConnector = {
		currentPreferences: app.currentPreferences,
		updatePreferences: () => EMPTY,
		updatePageSizes: (pageSizes : IPreferedPageSizes) => { preferences$.next({pageSizes}); return of(null) },
		updateFilters: (filters: IUserFilters) => { filters$.next(filters); return of(null) },
	}

	app.currentPreferences.pipe(take(1), filter(p => p && p.pageSizes && !!p.pageSizes[domain])).subscribe(p => {
		const pageSize = p.pageSizes[domain] || AppConstants.LIST_PAGE_SIZE_DEFAULT
		console.debug(`[user-prefs] restoring ${domain} preferred page size: ${pageSize}`)
		patchState(state, setListPaging({ pageSize }, collection))
	})

	return prefsManager
}

export function setListLoading<Prop extends string>(
	prop?: Prop,
) {
	return (state) => {
		if (prop) {
			return { ...state, [`${prop}CallState`]: 'loading' } as NamedListStateSlice<Prop>;
		} else {
			return { ...state, callState: 'loading' };
		}
	}
}

export function setListLoaded<Prop extends string>(
	prop?: Prop,
) {
	return (state) => {
		if (prop) {
			return { ...state, [`${prop}CallState`]: 'loaded' } as NamedListStateSlice<Prop>;
		} else {
			return { ...state, callState: 'loaded' };
		}
	}
}

export function setListError<Prop extends string>(
	rawError: Error,
	prop?: Prop,
) {
	const error = rawError.toString();
	
	return (state) => {
		if (prop) {
			return { ...state, [`${prop}CallState`]: { error } } as NamedListStateSlice<Prop>;
		} else {
			return { ...state, callState: { error } };
		}
	}
}

export function setList<Item, Prop extends string>(
	list: IList<Item>,
	prop?: Prop,
) {
	return (state) => {
		if (prop) {
			return { ...state, [`${prop}List`]: list } as NamedListStateSlice<Prop>;
		} else {
			return { ...state, list };
		}
	}
}

export function updateListItems<Item, Prop extends string>(
	items: Item[],
	prop?: Prop,
) {
	return (state) => {
		if (prop) {
			return { ...state, [`${prop}List`]: { ...state[`${prop}List`], items } } as NamedListStateSlice<Prop>;
		} else {
			return { ...state, list: { ...state.list, items } };
		}
	}
}

export function updateEachListItem<Item, Prop extends string>(
	fromItem: (item: Item) => Item,
	prop?: Prop,
) {
	return (state) => {
		if (prop) {
			const items = state[`${prop}List`].items.map(fromItem)
			return { ...state, ...updateListItems(items, prop)(state) };
		} else {
			const items = state.list.items.map(fromItem)
			return { ...state, ...updateListItems(items)(state) };
		}
	}
}

export function setListPaging<Sort, Prop extends string>(
	paging: ListPaging<Sort>,
	prop?: Prop,
) {
	return (state) => {
		if (prop) {
			const p = state[`${prop}Paging`];
			return { ...state, [`${prop}Paging`]: { ...p, ...paging} } as NamedListStateSlice<Prop>;
		} else {
			const p = state['paging'];
			return { ...state, paging: { ...p, ...paging} };
		}
	}
}

export function addListFilters<Prop extends string>(
	filters: IFilter[],
	prop?: Prop
) {
	return (state) => {
		if (prop) {
			const p = state[`${prop}Filters`];
			return { ...state, [`${prop}Filters`]: [...p, ...filters] } as NamedListStateSlice<Prop>;
		}
		const p = state['filters'];
		return { ...state, filters: [...p, ...filters] };
	}
}

export function removeListFilters<Prop extends string>(
	filtersToRemove: IFilter[],
	prop?: Prop
) {
	return (state) => {
		const sProp = prop ? `${prop}Filters` : 'filters';
		let filters = [...state[sProp]];
		filtersToRemove.forEach(r => {
			// first try to remove by ref
			let index = filters.indexOf(r);
			if (index > -1) return filters.splice(index, 1);
			// then try to remove by id
			index = filters.findIndex(f => f.id === r.id);
			if (index > -1) return filters.splice(index, 1);
			// last, try to remove by name
			filters = filters.filter(f => f.field === r.field)
		})
		return { ...state, [sProp]: filters } as NamedListStateSlice<Prop>;
	}
}

export function resetListFilters<Prop extends string>(
	prop?: Prop
) {
	return (state) => {
		if (prop) {
			return { ...state, [`${prop}Filters`]: [] } as NamedListStateSlice<Prop>;
		}
		return { ...state, filters: [] };
	}
}

export interface CallStatedListOptions {
	append?: boolean;
	stateOnly?: boolean;
	setLoading?: boolean | 'first' | 'empty';
}

export async function callStatedList<
	E,
	C extends string,
	S extends object
>(p: Observable<E>, store: S, collection?: C, options?: CallStatedListOptions & { stateOnly: true }): Promise<E>;
export async function callStatedList<
	I,
	E extends IList<I>,
	C extends string,
	S extends WritableStateSource<Record<`${C}List`, E>>
>(p: Observable<E>, store: S, collection?: C, options?: CallStatedListOptions): Promise<E>;
export async function callStatedList<
	I,
	E extends IList<I>,
	C extends string,
	S extends WritableStateSource<Record<`${C}List`, E>>
>(p: Observable<E>, store: S, collection?: C, options?: CallStatedListOptions): Promise<E> {
	const { listKey, callStateKey: listStateKey } = getListStateKeys({ collection });
	const callState: Signal<CallState> = store[listStateKey];
	const current: Signal<E> = store[listKey];
	const opts = {
		append: false,
		stateOnly: false,
		setLoading: true,
		...options,
	}

	if (opts.setLoading === true
		|| (opts.setLoading === 'first' && callState() === 'init')
		|| (opts.setLoading === 'empty' && current().items.length === 0)
	) {
		patchState(store, setListLoading(collection))
	}

	return firstValueFrom(from(p.pipe(
		map(response => {
			patchState(store, setListLoaded(collection))

			const previousItems = store[listKey]().items
			const list = opts.append
				? { ...response, items: previousItems.concat(response.items) }
				: response

			if (!opts.stateOnly) patchState(store, { [listKey]: list } as any)

			return list;
		}),
		catchError((error: any) => {
			patchState(store, setListError(error, collection))
			return throwError(() => error)
		})
	)))
}
